import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { matchPath, useLocation } from 'react-router-dom';
import { useIsMobile } from '../../../hooks/useIsMobile';

/** More-specific patterns first so matchPath picks the right desktop page file. */
const routeComponentMap: [string, string][] = [
  ['/', 'HomePage.tsx'],
  ['/about', 'AboutPage.tsx'],
  ['/magazines', 'MagazineLandingPage.tsx'],
  ['/Audio', 'AudioLandingPage.tsx'],
  // ['/test', 'TestPage.tsx'], // dev-only — uncomment when /test route is enabled

  // Books
  ['/books', 'BooksListPage.tsx'],
  ['/books/special', 'BooksListPage.tsx'],
  ['/books/author/:id', 'BooksListPage.tsx'],
  ['/books/language/:langid', 'BooksListPage.tsx'],
  ['/books/category/:catid', 'BooksListPage.tsx'],
  ['/books/special/:bookid', 'BookDetailPage.tsx'],
  ['/books/author/:authorId/:slug', 'BookDetailPage.tsx'],
  ['/books/language/:langId/:slug', 'BookDetailPage.tsx'],
  ['/books/category/:catId/:slug', 'BookDetailPage.tsx'],
  ['/books/:bookid', 'BookDetailPage.tsx'],
  ['/books/:slug', 'BookDetailPage.tsx'],

  // Reader
  ['/reader/:type/:id', 'Epub.tsx'],

  // Vivekvani
  ['/magazines/vivekvani', 'VivekVani.tsx'],
  ['/magazines/vivekvani/special', 'VivekVani.tsx'],
  ['/magazines/vivekvani/author/:id', 'VivekVani.tsx'],
  ['/magazines/vivekvani/language/:langid', 'VivekVani.tsx'],
  ['/magazines/vivekvani/:cat/:langid', 'VivekVani.tsx'],
  ['/magazines/vivekvani/special/:vaniid', 'VivekvaniDetailPage.tsx'],
  ['/magazines/vivekvani/author/:id/:vaniid', 'VivekvaniDetailPage.tsx'],
  ['/magazines/vivekvani/category/:catid', 'VivekvaniDetailPage.tsx'],
  ['/magazines/vivekvani/language/:langid', 'VivekvaniDetailPage.tsx'],
  ['/magazines/vivekvani/:slug', 'VivekvaniDetailPage.tsx'],

  // Kalyan
  ['/magazines/kalyan', 'KalyansPage.tsx'],
  ['/magazines/kalyan/category/:catid', 'KalyansPage.tsx'],
  ['/magazines/kalyan/special', 'KalyansPage.tsx'],
  ['/magazines/kalyan/author/:id', 'KalyansPage.tsx'],
  ['/magazines/kalyan/language/:langid', 'KalyansPage.tsx'],
  ['/magazines/kalyan/special/:kalyanid', 'KalyanDetailPage.tsx'],
  ['/magazines/kalyan/author/:id/:kalyanid', 'KalyanDetailPage.tsx'],
  ['/magazines/kalyan/category/:catid/:kalyanid', 'KalyanDetailPage.tsx'],
  ['/magazines/kalyan/language/:langid/:kalyanid', 'KalyanDetailPage.tsx'],
  ['/magazines/kalyan/:slug', 'KalyanDetailPage.tsx'],

  // Kalyanakalpataru
  ['/magazines/kalyanakalpataru', 'KalpataruPage.tsx'],
  ['/magazines/kalyanakalpataru/category/:catid', 'KalpataruPage.tsx'],
  ['/magazines/kalyanakalpataru/special', 'KalpataruPage.tsx'],
  ['/magazines/kalyanakalpataru/author/:id', 'KalpataruPage.tsx'],
  ['/magazines/kalyanakalpataru/language/:langid', 'KalpataruPage.tsx'],
  ['/magazines/kalyanakalpataru/special/:kalyanid', 'KalpataruDetailPage.tsx'],
  ['/magazines/kalyanakalpataru/author/:id/:kalyanid', 'KalpataruDetailPage.tsx'],
  ['/magazines/kalyanakalpataru/category/:catid/:kalyanid', 'KalpataruDetailPage.tsx'],
  ['/magazines/kalyanakalpataru/language/:langid/:kalyanid', 'KalpataruDetailPage.tsx'],
  ['/magazines/kalyanakalpataru/:slug', 'KalpataruDetailPage.tsx'],

  // Geetgovind
  ['/magazines/geetgovind', 'GeetgovindPage.tsx'],
  ['/magazines/geetgovind/category/:catid', 'GeetgovindPage.tsx'],
  ['/magazines/geetgovind/special', 'GeetgovindPage.tsx'],
  ['/magazines/geetgovind/author/:id', 'GeetgovindPage.tsx'],
  ['/magazines/geetgovind/language/:langid', 'GeetgovindPage.tsx'],
  ['/magazines/geetgovind/special/:id', 'GeetGovindDetailPage.tsx'],
  ['/magazines/geetgovind/category/:catid/:id', 'GeetGovindDetailPage.tsx'],
  ['/magazines/geetgovind/language/:langid/:id', 'GeetGovindDetailPage.tsx'],
  ['/magazines/geetgovind/:slug', 'GeetGovindDetailPage.tsx'],

  // Pravachans / Audios / DailySatsang
  ['/pravachans', 'PravachansPage.tsx'],
  ['/pravachans/special', 'PravachansPage.tsx'],
  ['/pravachans/author/:id', 'PravachansPage.tsx'],
  ['/pravachans/category/:catid', 'PravachansPage.tsx'],
  ['/pravachans/language/:langid', 'PravachansPage.tsx'],
  ['/pravachans/:cat/:id', 'AudioPlayerPage.tsx'],
  ['/pravachans/:slug', 'AudioPlayerPageNew.tsx'],
  ['/pravachans/:id', 'AudioPlayerPageNew.tsx'],
  ['/audios', 'AudiosPage.tsx'],
  ['/audios/special', 'AudiosPage.tsx'],
  ['/audios/author/:id', 'AudiosPage.tsx'],
  ['/audios/category/:catid', 'AudiosPage.tsx'],
  ['/audios/language/:langid', 'AudiosPage.tsx'],
  ['/audios/:cat/:id', 'AudioPlayerPage.tsx'],
  ['/audios/:slug', 'AudioPlayerPageNew.tsx'],
  ['/audios/:id', 'AudioPlayerPageNew.tsx'],
  ['/DailySatsang', 'DailySatsang.tsx'],
  ['/DailySatsang/:slug', 'AudioPlayerPageNew.tsx'],
  ['/DailySatsang/:id', 'AudioPlayerPageNew.tsx'],
  ['/DailySatsang/*', 'AudioPlayerPage.tsx'],

  // Videos — static segments before :slug
  ['/videos', 'VideoListPage.tsx'],
  ['/videos/author/:id', 'VideoListPage.tsx'],
  ['/videos/category/:id', 'VideoListPage.tsx'],
  ['/videos/video/:id', 'VideoListPage.tsx'],
  ['/videos/:slug', 'VideoDetailsPage.tsx'],

  // Articles — nested author/special/category/language before list + :slug
  ['/articles', 'ArticlesPage.tsx'],
  ['/articles/special', 'ArticlesPage.tsx'],
  ['/articles/author/:id', 'ArticlesPage.tsx'],
  ['/articles/category/:catid', 'ArticlesPage.tsx'],
  ['/articles/language/:langid', 'ArticlesPage.tsx'],
  ['/articles/special/:id', 'ArticlesDetail.tsx'],
  ['/articles/author/:id/:articleid', 'redirect /articles/:slug'],
  ['/articles/category/:catId/:slug', 'redirect /articles/:slug'],
  ['/articles/language/:langId/:slug', 'redirect /articles/:slug'],
  ['/articles/:slug', 'ArticlesDetail.tsx'],

  // Divine quotes / legal / misc
  ['/divinequotes', 'DivineQuotePage.tsx'],
  ['/divinequotes/:id', 'QuoteDetailsPage.tsx'],
  ['/privacypolicy', 'PrivacyPolicyPage.tsx'],
  ['/termsofuse', 'TermsOfUsePage.tsx'],
  ['/faq', 'FaqPage.tsx'],
  ['/promotion', 'PromotionPage.tsx'],
  ['/author/:id', 'AuthorsPage.tsx'],
  ['/searchdata/:searchCategory/:search', 'SearchDataPage.tsx'],
  ['/searchdata/:searchCategory/', 'SearchDataPage.tsx'],
  ['/searchdata/:searchCategory/*', 'SearchDataPage.tsx'],
  ['/profile/', 'Profile.tsx'],
  ['/profile/My-Favourites', 'ProfileFav.tsx'],
  ['/profile/feedback', 'ProfileContact.tsx'],
  ['/Login/*', 'NewLoginPage.tsx'],
  ['/pdf-viewer', 'PDFViewer.tsx'],
  ['/webView', 'WebView.tsx'],
  ['/fullimage', 'FullImage.tsx'],
];

function getPageFile(pathname: string): string {
  for (const [pattern, component] of routeComponentMap) {
    if (matchPath(pattern, pathname)) {
      return component;
    }
  }
  return '—';
}

/**
 * Development-only fixed bar showing the current desktop route and component file name.
 * Hidden on mobile (MobileDevPageInfoBar covers that) and stripped from production builds via NODE_ENV.
 */
const DesktopDevPageInfoBar: React.FC = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const isDev = process.env.NODE_ENV === 'development';
  const pathLabel = `${location.pathname}${location.search || ''}`;
  const shouldShow = isDev && !isMobile;
  const pageFile = useMemo(() => getPageFile(location.pathname), [location.pathname]);
  // Session-only dismiss — resets on full page refresh (no storage)
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (!shouldShow || isDismissed) return;
    const logdetails: string[] = [];
    logdetails.push('dev_page_info_bar_visible');
    logdetails.push(`path=${pathLabel}`);
    logdetails.push(`component=${pageFile}`);
    console.log('[DesktopDevPageInfoBar]', logdetails.join(' | '));
  }, [shouldShow, isDismissed, pathLabel, pageFile]);

  /** Hide bar for current SPA session; reappears after refresh */
  const handleDismiss = () => {
    const logdetails: string[] = [];
    logdetails.push('action=dismiss_dev_page_info_bar');
    logdetails.push(`path=${pathLabel}`);
    logdetails.push(`component=${pageFile}`);
    console.log('[DesktopDevPageInfoBar]', logdetails.join(' | '));
    setIsDismissed(true);
  };

  if (!shouldShow || isDismissed) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '40px',
        backgroundColor: '#0ea5e9',
        color: '#000000',
        position: 'fixed',
        bottom: 0,
        left: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        px: 2,
        py: 0.5,
        pl: 5,
        boxSizing: 'border-box',
        pointerEvents: 'none',
      }}
      role="status"
      aria-label={`Development page info: ${pageFile}`}
    >
      <Box
        component="span"
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          fontFamily: 'chanakyaUni, sans-serif',
          textAlign: 'left',
          wordBreak: 'break-all',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {pageFile} | {pathLabel}
      </Box>
      <IconButton
        aria-label="Close development page info bar"
        onClick={handleDismiss}
        size="small"
        sx={{
          position: 'absolute',
          left: 4,
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#000000',
          pointerEvents: 'auto',
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default DesktopDevPageInfoBar;
